import { useState, useEffect } from 'react';
import './index.css';
import { getMe, listCustomers, getAlerts } from './services/portalApi';
import Sidebar   from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import Topbar    from './components/Topbar';
import Login     from './pages/Login';
import Dashboard from './pages/Dashboard';
import Numbers   from './pages/Numbers';
import Routing   from './pages/Routing';
import PlatformsPage from './pages/Platforms';
import Analytics from './pages/Analytics';
import AlertsPage from './pages/Alerts';
import Settings  from './pages/Settings';
import Security  from './pages/Security';
import VoxboneRouting from './pages/VoxboneRouting';
import BandwidthRouting from './pages/BandwidthRouting';
import Admin     from './pages/Admin';
import Sites     from './pages/Sites';
import UsersPage from './pages/Users';
import TeamsCallQuality from './pages/TeamsCallQuality';
import CallQueues from './pages/CallQueues';
import AutoAttendants from './pages/AutoAttendants';

const PAGES = {
  dashboard:   Dashboard,
  numbers:     Numbers,
  routing:     Routing,
  voxbone:     VoxboneRouting,
  bwrouting:   BandwidthRouting,
  platforms:   PlatformsPage,
  analytics:   Analytics,
  callquality: TeamsCallQuality,
  callqueues:  CallQueues,
  autoattendants: AutoAttendants,
  alerts:      AlertsPage,
  settings:    Settings,
  security:    Security,
  admin:       Admin,
  sites:       Sites,
  users:       UsersPage,
};

function loadAckedIds() {
  try { return new Set(JSON.parse(localStorage.getItem('acked_alert_ids') || '[]')); }
  catch { return new Set(); }
}

function saveAckedIds(set) {
  localStorage.setItem('acked_alert_ids', JSON.stringify([...set]));
}

export default function App() {
  const [user,           setUser]           = useState(null);
  const [authChecked,    setAuthChecked]    = useState(false);
  const [page,           setPage]           = useState('dashboard');
  const [refreshKey,     setRefreshKey]     = useState(0);
  const [customers,      setCustomers]      = useState([]);
  const [viewAsCustomer, setViewAsCustomer] = useState(null);
  const [alertCount,     setAlertCount]     = useState(0);
  const [liveAlerts,     setLiveAlerts]     = useState([]);
  const [ackedIds,       setAckedIds]       = useState(loadAckedIds);
  const [mobileNavOpen,  setMobileNavOpen]  = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    if (!token) { setAuthChecked(true); return; }
    getMe()
      .then(data => setUser(data.user))
      .catch(() => localStorage.removeItem('portal_token'))
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    if (!user?.isAdmin) return;
    listCustomers()
      .then(data => setCustomers((data.customers || []).filter(c => !c.isAdmin)))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchAlerts = () =>
      getAlerts().then(d => setLiveAlerts(d.alerts || [])).catch(() => {});
    fetchAlerts();
    const id = setInterval(fetchAlerts, 30000);
    return () => clearInterval(id);
  }, [user]);

  // Recompute badge: unacknowledged alerts only
  useEffect(() => {
    setAlertCount(liveAlerts.filter(a => !ackedIds.has(a.id)).length);
  }, [liveAlerts, ackedIds]);

  function handleAcknowledge(id) {
    setAckedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      saveAckedIds(next);
      return next;
    });
  }

  function handleAcknowledgeAll() {
    setAckedIds(prev => {
      const next = new Set([...prev, ...liveAlerts.map(a => a.id)]);
      saveAckedIds(next);
      return next;
    });
  }

  function handleLogout() {
    localStorage.removeItem('portal_token');
    setUser(null);
    setPage('dashboard');
    setViewAsCustomer(null);
  }

  function handleNav(id) {
    if (id === 'admin' && !user?.isAdmin) return;
    setPage(id);
    setMobileNavOpen(false);
  }

  function handleViewAsChange(customer) {
    setViewAsCustomer(customer);
    setRefreshKey(k => k + 1);
  }

  if (!authChecked) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--muted-3)', fontSize: 13 }}>Loading…</div>
      </div>
    );
  }

  if (!user) return <Login onLogin={u => setUser(u)} />;

  // Non-admin customers only see their enabled modules; if the current page
  // isn't allowed, fall back to the first module they can see.
  const allowed = !user.isAdmin && Array.isArray(user.enabledModules) ? user.enabledModules : null;
  let safePage = page === 'admin' && !user.isAdmin ? 'dashboard' : page;
  if (allowed && safePage !== 'admin' && !allowed.includes(safePage)) {
    safePage = allowed[0] || 'dashboard';
  }
  const Page     = PAGES[safePage] || Dashboard;

  return (
    <div className="app">
      {mobileNavOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileNavOpen(false)} />
      )}
      <Sidebar
        active={safePage}
        onNav={handleNav}
        user={user}
        onLogout={handleLogout}
        customers={customers}
        viewAsCustomer={viewAsCustomer}
        onViewAsChange={handleViewAsChange}
        alertCount={alertCount}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="main">
        <Topbar
          page={safePage}
          onRefresh={() => setRefreshKey(k => k + 1)}
          user={user}
          onNav={handleNav}
          alertCount={alertCount}
          onMenuToggle={() => setMobileNavOpen(o => !o)}
        />
        <ErrorBoundary resetKey={safePage + ':' + refreshKey}>
        <Page
          key={refreshKey}
          onNav={handleNav}
          user={user}
          viewAsCustomer={viewAsCustomer}
          customers={customers}
          liveAlerts={liveAlerts}
          ackedIds={ackedIds}
          onAcknowledge={handleAcknowledge}
          onAcknowledgeAll={handleAcknowledgeAll}
        />
        </ErrorBoundary>
      </div>
    </div>
  );
}
